import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { retry } from '@reduxjs/toolkit/query/react';
import i18n from 'i18next';

import { logoutByForce } from 'redux/user/user.thunk';
import { setNotification } from 'redux/notification/notification.slice';
import { store } from 'store';
import {
  createTokenRefresher,
  createWithRefreshLock,
  getSentToken,
} from '../tokenRefresh/tokenRefresh.helper';
import {
  getRefreshToken,
  getToken,
  removeRefreshToken,
  removeToken,
  storeRefreshToken,
  storeToken,
} from '../user/user.helper';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URI,
  isJsonContentType: (headers) => {
    const contentType = headers.get('content-type');
    return Boolean(
      contentType?.includes('application/json') ||
      contentType?.includes('application/ld+json') ||
      contentType?.includes('application/merge-patch+json')
    );
  },
  prepareHeaders: (headers: Headers, { arg }) => {
    // token in localStorage :(
    const token = getToken();
    const method = typeof arg === 'string' ? 'GET' : arg?.method || 'GET';

    headers.set('accept', 'application/ld+json');
    headers.set('content-type', 'application/ld+json');
    // PATCH requests need a specific content type
    if (method === 'PATCH') {
      headers.set('Content-Type', 'application/merge-patch+json');
    }
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const customBaseQueryFn: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  // if method is PATCH, we need to stringify the body
  // for PUT methods, RTK Query already serializes the body
  if (typeof args === 'object' && args?.method === 'PATCH') {
    if (
      args.body &&
      typeof args.body === 'object' &&
      !(args.body instanceof FormData)
    ) {
      args.body = JSON.stringify(args.body);
    }
  }

  return rawBaseQuery(args, api, extraOptions);
};

export const noAuthCustomBaseQueryFn = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URI,
  isJsonContentType: (headers) => {
    const contentType = headers.get('content-type');
    return Boolean(
      contentType?.includes('application/json') ||
      contentType?.includes('application/ld+json') ||
      contentType?.includes('application/merge-patch+json')
    );
  },
  prepareHeaders: (headers: Headers) => {
    headers.set('accept', 'application/ld+json');
    headers.set('content-type', 'application/ld+json');
    return headers;
  },
});

const resetUser = async () => {
  removeRefreshToken();
  removeToken();
  return store.dispatch(logoutByForce());
};

const tokenRefresher = createTokenRefresher({
  withRefreshLock: createWithRefreshLock(),
  storage: {
    getToken,
    getRefreshToken,
    storeTokens: (token, refreshToken) => {
      storeToken(token);
      storeRefreshToken(refreshToken);
    },
  },
});

export interface ISessionExtraOptions {
  /** the endpoint is used before login (login, password forgot, 2FA) */
  withoutSession?: boolean;
}

const NO_SESSION_ERROR: FetchBaseQueryError = {
  status: 'CUSTOM_ERROR',
  error: 'No session',
};

/**
 * interceptor that tries to reauthenticate, if token is no longer valid
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  ISessionExtraOptions
> = async (args, api, extraOptions) => {
  // if this tab is refreshing, wait for it before making the new request
  await tokenRefresher.waitForRefresh();
  // without a session the request can only end in a 401, so it is not sent.
  // If the token vanished while this tab still shows a logged in user, end
  // the session here so the app redirects instead of failing silently
  if (!getToken() && !extraOptions?.withoutSession) {
    if (store.getState().user.isLoggedIn) {
      await resetUser();
    }
    return retry.fail(NO_SESSION_ERROR);
  }
  const result = await customBaseQueryFn(args, api, extraOptions);
  if (result.error?.status !== 401) {
    return result;
  }

  const recovery = await tokenRefresher.recoverFromUnauthorized(
    getSentToken(result.meta),
    async (refreshToken) =>
      customBaseQueryFn(
        {
          url: 'api/token/refresh',
          body: {
            refresh_token: refreshToken,
          },
          method: 'POST',
        },
        api,
        extraOptions
      )
  );

  if (recovery.type === 'retry') {
    return customBaseQueryFn(args, api, extraOptions);
  }
  // the session stays, the retry wrapper tries again with backoff. The
  // notification slice holds one message, so retries and parallel requests
  // overwrite it instead of stacking
  if (recovery.type === 'unreachable') {
    store.dispatch(
      setNotification({
        type: 'error',
        message: i18n.t('tokenRefresh.unreachable', { ns: 'errors' }),
      })
    );
    return { error: recovery.error, meta: recovery.meta };
  }

  await resetUser();
  // stop retrying
  return retry.fail(result.error, result.meta);
};
